import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import app from '../services/firebase';

// API Configuration defaults
export const API_CONFIG = {
  development: {
    baseUrl: 'https://api-63xrmvevdq-uc.a.run.app', // Local API for development
  },
  production: {
    baseUrl: 'https://api.careva.ai',
  },
};

let cachedBaseUrl: string | null = null;

export const getApiBaseUrl = async (): Promise<string> => {
  if (cachedBaseUrl) {
    return cachedBaseUrl;
  }

  if (process.env.REACT_APP_API_BASE_URL) {
    cachedBaseUrl = process.env.REACT_APP_API_BASE_URL;
    return cachedBaseUrl;
  }

  try {
    const rc = getRemoteConfig(app);
    rc.settings = {
      minimumFetchIntervalMillis: process.env.NODE_ENV === 'development' ? 0 : 60 * 60 * 1000,
      fetchTimeoutMillis: 5_000,
    } as typeof rc.settings;

    rc.defaultConfig = {
      api_base_url: API_CONFIG[process.env.NODE_ENV === 'production' ? 'production' : 'development'].baseUrl,
      app_env: process.env.NODE_ENV || 'development',
    } as Record<string, string>;

    await fetchAndActivate(rc);
    const apiBaseUrl = getValue(rc, 'api_base_url').asString();
    cachedBaseUrl = apiBaseUrl || API_CONFIG.development.baseUrl;
    return cachedBaseUrl;
  } catch (error) {
    // Fallback to defaults on failure
    const environment = process.env.NODE_ENV === 'production' ? 'production' : 'development';
    cachedBaseUrl = API_CONFIG[environment as keyof typeof API_CONFIG].baseUrl;
    return cachedBaseUrl;
  }
};

// API Client class
class ApiClient {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ data: T }> {
    const baseUrl = await getApiBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    
    const token = localStorage.getItem('authToken');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return { data };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An unexpected error occurred');
    }
  }

  async get<T>(endpoint: string): Promise<{ data: T }> {
    return this.makeRequest<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: any): Promise<{ data: T }> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: any): Promise<{ data: T }> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<{ data: T }> {
    return this.makeRequest<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
